import { useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronLeft,
  ChevronRight,
  Bell,
  X,
  DollarSign
} from 'lucide-react';

/**
 * SmartAlertsCarousel Component
 * Horizontal scrolling carousel of intelligent alerts
 */
export default function SmartAlertsCarousel({ alerts = [], loading = false, onDismiss, tipoCambio }) {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  // Maneja navegación con fragments (#anchor): si ya estamos en la misma página,
  // hace scrollIntoView; si no, navega y deja que el browser haga el scroll.
  const handleAccion = (e, href) => {
    if (!href || !href.includes('#')) return; // navegación normal
    const [path, anchor] = href.split('#');
    if (path === location.pathname) {
      e.preventDefault();
      const el = document.getElementById(anchor);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Flash visual breve para llamar la atención al elemento destino
        el.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2', 'rounded-xl');
        setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2', 'rounded-xl'), 1500);
      }
    }
  };

  // Filter out dismissed alerts
  const visibleAlerts = alerts.filter(alert => !dismissedAlerts.has(alert.id));

  const handleDismiss = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
    onDismiss?.(alertId);
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-400">Alertas Inteligentes</span>
          </div>
          {tipoCambio && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">
                Tipo de Cambio: ₡{tipoCambio?.venta?.toFixed(2) || tipoCambio}
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse flex-shrink-0 w-64 h-24 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (visibleAlerts.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-gray-900">Alertas Inteligentes</span>
          </div>
          {tipoCambio && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">
                Tipo de Cambio: ₡{tipoCambio?.venta?.toFixed(2) || tipoCambio}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-center py-6 text-gray-500">
          <div className="text-center">
            <div className="h-10 w-10 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-2">
              <AlertCircle className="h-5 w-5 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-gray-900">Sin alertas pendientes</p>
            <p className="text-xs text-gray-500">Todo esta bajo control</p>
          </div>
        </div>
      </div>
    );
  }

  // Alert type configurations
  const alertConfig = {
    danger: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      icon: AlertTriangle
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      icon: AlertTriangle
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      icon: Info
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium text-gray-900">
            Alertas Inteligentes
          </span>
          <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
            {visibleAlerts.length}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Exchange Rate */}
          {tipoCambio && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">
                Tipo de Cambio: ₡{tipoCambio?.venta?.toFixed(2) || tipoCambio}
              </span>
            </div>
          )}

          {/* Navigation buttons */}
          {visibleAlerts.length > 2 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => scroll('left')}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Alerts carousel */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {visibleAlerts.map((alert) => {
          const config = alertConfig[alert.tipo] || alertConfig.info;
          const Icon = config.icon;

          return (
            <div
              key={alert.id}
              className={`flex-shrink-0 w-72 p-3 rounded-lg border ${config.bg} ${config.border} relative group`}
            >
              {/* Dismiss button */}
              <button
                onClick={() => handleDismiss(alert.id)}
                className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${config.iconBg}`}>
                  <Icon className={`h-4 w-4 ${config.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate pr-6">
                    {alert.titulo}
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                    {alert.descripcion}
                  </p>
                  {alert.accion && (
                    <Link
                      to={alert.accion.href}
                      onClick={(e) => handleAccion(e, alert.accion.href)}
                      className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      {alert.accion.label}
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
